import React, { useRef, useCallback, forwardRef } from 'react';
import { Box, BoxProps } from '@mui/material';

// Type definitions for swipe events
export interface SwipeEventData {
  distance: number;
  deltaTime: number;
  velocity: number;
}

export interface SwipeConfig {
  /** Minimum distance in pixels to register as a swipe (default: 50) */
  minDistance?: number;
  /** Maximum time in milliseconds for a valid swipe (default: 1000) */
  maxTime?: number;
  /** Minimum velocity threshold for swipe detection (default: 0.3) */
  threshold?: number;
  /** Prevent default touchmove behavior (default: false) */
  preventDefaultTouchmove?: boolean;
  /** Enable mouse drag events for desktop (default: false) */
  enableMouseEvents?: boolean;
  /** Disable all swipe functionality (default: false) */
  disabled?: boolean;
}

export interface SwipeableBoxProps extends Omit<BoxProps, 'onTouchStart' | 'onTouchMove' | 'onTouchEnd' | 'onMouseDown' | 'onMouseMove' | 'onMouseUp' | 'onMouseLeave'> {
  /** Callback fired when user swipes left */
  onSwipeLeft?: (data: SwipeEventData) => void;
  /** Callback fired when user swipes right */
  onSwipeRight?: (data: SwipeEventData) => void;
  /** Callback fired when swipe gesture starts */
  onSwipeStart?: (clientX: number) => void;
  /** Callback fired when swipe gesture ends */
  onSwipeEnd?: (deltaX: number, deltaTime: number, velocity: number) => void;
  /** Configuration object for swipe behavior */
  swipeConfig?: SwipeConfig;
}

/**
 * Extended MUI Box component with swipe gesture support
 * Inherits all MUI Box props and adds swipe functionality for left/right gestures
 */
const SwipeableBox = forwardRef<HTMLDivElement, SwipeableBoxProps>(({
  onSwipeLeft,
  onSwipeRight,
  onSwipeStart,
  onSwipeEnd,
  swipeConfig = {},
  children,
  ...boxProps
}, ref) => {
  const {
    minDistance = 50,
    maxTime = 1000,
    threshold = 0.3,
    preventDefaultTouchmove = false,
    enableMouseEvents = false,
    disabled = false,
  } = swipeConfig;

  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);
  const startTime = useRef<number>(0);
  const isTracking = useRef<boolean>(false);

  const handleStart = useCallback((clientX: number): void => {
    if (disabled) return;

    touchStartX.current = clientX;
    touchEndX.current = clientX;
    startTime.current = Date.now();
    isTracking.current = true;

    onSwipeStart?.(clientX);
  }, [onSwipeStart, disabled]);

  const handleMove = useCallback((clientX: number): void => {
    if (!isTracking.current || disabled) return;

    touchEndX.current = clientX;
  }, [disabled]);

  const handleEnd = useCallback((): void => {
    if (!isTracking.current || disabled) return;

    const deltaX = touchStartX.current - touchEndX.current;
    const deltaTime = Date.now() - startTime.current;
    const distance = Math.abs(deltaX);
    const velocity = distance / deltaTime;

    isTracking.current = false;

    // Check if swipe meets criteria
    const isValidSwipe = distance >= minDistance &&
                        deltaTime <= maxTime &&
                        velocity >= threshold;

    if (isValidSwipe) {
      const swipeData: SwipeEventData = {
        distance,
        deltaTime,
        velocity,
      };

      if (deltaX > 0) {
        onSwipeLeft?.(swipeData);
      } else {
        onSwipeRight?.(swipeData);
      }
    }

    onSwipeEnd?.(deltaX, deltaTime, velocity);
  }, [
    minDistance,
    maxTime,
    threshold,
    onSwipeLeft,
    onSwipeRight,
    onSwipeEnd,
    disabled,
  ]);

  // Touch event handlers
  const handleTouchStart = useCallback((e: React.TouchEvent<HTMLDivElement>): void => {
    const touch = e.targetTouches[0];
    handleStart(touch.clientX);
  }, [handleStart]);

  const handleTouchMove = useCallback((e: React.TouchEvent<HTMLDivElement>): void => {
    if (preventDefaultTouchmove) {
      e.preventDefault();
    }
    const touch = e.targetTouches[0];
    handleMove(touch.clientX);
  }, [handleMove, preventDefaultTouchmove]);

  const handleTouchEnd = useCallback((): void => {
    handleEnd();
  }, [handleEnd]);

  // Mouse event handlers (optional)
  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>): void => {
    if (!enableMouseEvents) return;
    handleStart(e.clientX);
  }, [enableMouseEvents, handleStart]);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>): void => {
    if (!enableMouseEvents) return;
    handleMove(e.clientX);
  }, [enableMouseEvents, handleMove]);

  const handleMouseUp = useCallback((): void => {
    if (!enableMouseEvents) return;
    handleEnd();
  }, [enableMouseEvents, handleEnd]);

  const handleMouseLeave = useCallback((): void => {
    if (!enableMouseEvents) return;
    isTracking.current = false;
  }, [enableMouseEvents]);

  // Combine event handlers
  const eventHandlers = {
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
    ...(enableMouseEvents && {
      onMouseDown: handleMouseDown,
      onMouseMove: handleMouseMove,
      onMouseUp: handleMouseUp,
      onMouseLeave: handleMouseLeave,
    }),
  };

  return (
    <Box
      ref={ref}
      {...boxProps}
      {...eventHandlers}
      sx={{
        // Add touch-action: none to prevent default browser behaviors
        touchAction: 'pan-y',
        // Add cursor for mouse events
        ...(enableMouseEvents && { cursor: 'grab' }),
        // User can still override with custom sx
        ...boxProps.sx,
      }}
    >
      {children}
    </Box>
  );
});

SwipeableBox.displayName = 'SwipeableBox';

export default SwipeableBox;
